/**
 * Console override helper that controls logging based on environment
 * - Logs are blocked on production feegow.com domains
 * - Logs are allowed on dev/staging subdomains (app-homolog, app*dev*)
 * - Logs are allowed on localhost and non-feegow domains
 * - Keeps console.error intact for critical errors
 */

type ConsoleMethods = "log" | "warn";

interface StoredConsoleMethods {
  log: typeof console.log;
  warn: typeof console.warn;
}

/**
 * Check if the current environment allows logging
 * - Block on *.feegow.com (production)
 * - Allow on app-homolog.feegow.com or app*dev*feegow.com (staging/dev)
 * - Allow on localhost and non-feegow domains
 */
const isLoggingAllowed = (): boolean => {
  if (typeof window === "undefined") {
    return true; // Allow logging in server-side rendering
  }

  const hostname = window.location.hostname.toLowerCase();

  // Check if it's a feegow.com domain
  const isFeegowDomain = hostname.endsWith("feegow.com") || hostname === "feegow.com";

  if (!isFeegowDomain) {
    // Not a feegow domain - allow logging (localhost, other environments)
    return true;
  }

  // It's a feegow domain - check if it's a dev/staging subdomain
  const isHomolog = hostname.includes("homolog");
  const isDev = hostname.includes("dev");

  // Allow logging only on homolog or dev subdomains
  return isHomolog || isDev;
};

/**
 * Create a wrapped console method that respects environment
 */
const createWrappedMethod = (
  originalMethod: (...args: unknown[]) => void,
  _methodName: ConsoleMethods
): ((...args: unknown[]) => void) => {
  return (...args: unknown[]): void => {
    if (isLoggingAllowed()) {
      originalMethod(...args);
    }
    // In production feegow.com, do nothing (silent)
  };
};

/**
 * Store original console methods before overriding
 */
export const storeOriginalConsoleMethods = (): void => {
  if (typeof window !== "undefined" && !(window as any).__originalConsoleMethods) {
    (window as any).__originalConsoleMethods = {
      log: console.log,
      warn: console.warn,
    } as StoredConsoleMethods;
  }
};

/**
 * Override console.log and console.warn globally
 * Only logs in development/staging environments
 * Keeps console.error intact for critical errors
 */
export const overrideConsoleLog = (): void => {
  if (typeof window === "undefined") {
    return;
  }

  // Store original methods before overriding
  storeOriginalConsoleMethods();

  const stored = (window as any).__originalConsoleMethods as StoredConsoleMethods;

  // Override console.log
  console.log = createWrappedMethod(stored.log, "log");

  // Override console.warn
  console.warn = createWrappedMethod(stored.warn, "warn");
};

/**
 * Restore original console methods
 */
export const restoreConsoleLog = (): void => {
  if (typeof window !== "undefined" && (window as any).__originalConsoleMethods) {
    const stored = (window as any).__originalConsoleMethods as StoredConsoleMethods;
    console.log = stored.log;
    console.warn = stored.warn;
  }
};

// Legacy export for backwards compatibility
export const storeOriginalConsoleLog = storeOriginalConsoleMethods;
